import type { OAuthUser } from '@/types';
import { routes } from '@/lib/constants';
import { getUserIdFromAuthToken } from '@/lib/auth/auth-token';
import type { AuthData } from '@/api/mutations/auth/response';

export interface GoogleIdTokenPayload {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
}

export interface GoogleSetupTokenPayload {
  authId?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  purpose?: string;
}

type NavigateFn = (
  to: string | { pathname?: string; search?: string },
  options?: { replace?: boolean; state?: unknown }
) => void;

const decodeJwtPayload = <T extends object>(token: string): T | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '='
    );
    return JSON.parse(atob(padded)) as T;
  } catch {
    return null;
  }
};

export const parseGoogleIdToken = (
  idToken: string
): GoogleIdTokenPayload | null => decodeJwtPayload(idToken);

export const parseGoogleSetupToken = (
  setupToken: string
): GoogleSetupTokenPayload | null => decodeJwtPayload(setupToken);

export const toOAuthUser = (
  payload: GoogleIdTokenPayload
): OAuthUser | null => {
  if (!payload.sub || !payload.email) return null;

  return {
    id: payload.sub,
    email: payload.email,
    verified_email: Boolean(payload.email_verified),
    name: payload.name || '',
    given_name: payload.given_name || '',
    family_name: payload.family_name || '',
    picture: payload.picture || '',
  };
};

/** Prefill profile fields from the Sellyt google-setup JWT when needed. */
export const toOAuthUserFromSetupToken = (
  setupToken: string
): OAuthUser | null => {
  const payload = parseGoogleSetupToken(setupToken);
  if (!payload?.email) return null;

  const firstName = payload.firstName || '';
  const lastName = payload.lastName || '';

  return {
    id: payload.authId != null ? String(payload.authId) : payload.email,
    email: payload.email,
    verified_email: true,
    name: [firstName, lastName].filter(Boolean).join(' '),
    given_name: firstName,
    family_name: lastName,
    picture: '',
  };
};

/** Response A — still needs complete-signup (profile). */
export const isGoogleSetupAuthData = (
  data?: Pick<AuthData, 'isNewAccount' | 'setupToken'> | null
): boolean => data?.isNewAccount === true || Boolean(data?.setupToken);

/** Phone OTP still required (login/google record uses isPhoneVerified). */
export const needsPhoneVerificationFromAuth = (
  data?: AuthData | null
): boolean => {
  const verified =
    data?.record?.isPhoneVerified ?? data?.isPhoneVerified ?? null;
  return verified !== true;
};

/** Resolve routing fields from Google login Response A or B. */
export const resolveGoogleLoginRouting = (data?: AuthData | null) => {
  const whatsappNumber = data?.record?.whatsappNumber?.trim() || undefined;

  return {
    isNewAccount: isGoogleSetupAuthData(data),
    hasBusinessSetup: Boolean(
      data?.record?.hasBusinessSetup ?? data?.hasBusinessSetup
    ),
    needsPhoneVerification: needsPhoneVerificationFromAuth(data),
    // Prefer JWT / top-level userId — record.id is authId, not tenant user id.
    userId: data?.userId ?? getUserIdFromAuthToken(),
    // Only returned when phone is unverified (backend omits it otherwise).
    identifier: whatsappNumber,
  };
};

export const navigateAfterGoogleAuth = (
  navigate: NavigateFn,
  {
    hasBusinessSetup,
    isNewAccount,
    needsPhoneVerification,
    userId,
    identifier,
    search = '',
  }: {
    hasBusinessSetup?: boolean;
    isNewAccount?: boolean;
    needsPhoneVerification?: boolean;
    userId?: number;
    identifier?: string;
    search?: string;
  } = {}
) => {
  // Response A — complete personal profile (then OTP → business).
  if (isNewAccount) {
    navigate(
      { pathname: routes.signUp, search },
      {
        replace: true,
        state: {
          step: 1,
          userId: userId ?? getUserIdFromAuthToken(),
        },
      }
    );
    return;
  }

  // Response B — session token already stored.
  if (hasBusinessSetup) {
    navigate(routes.root, { replace: true });
    return;
  }

  // Profile exists but WhatsApp/phone not verified yet.
  if (needsPhoneVerification !== false) {
    navigate(
      { pathname: routes.signUp, search },
      {
        replace: true,
        state: {
          step: 2,
          userId: userId ?? getUserIdFromAuthToken(),
          ...(identifier ? { identifier } : {}),
        },
      }
    );
    return;
  }

  // Phone verified, business not set up.
  navigate(
    { pathname: routes.signUp, search },
    {
      replace: true,
      state: {
        step: 3,
        userId: userId ?? getUserIdFromAuthToken(),
      },
    }
  );
};
